/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import { getApiUrl, getPortalUrl, getTroyUrl } from "constants/domain"

export const apiReducerPaths = {
  grabXApi: "grabXApi",
  foodMerchantApi: "foodMerchantApi",
  dpApi: "dpApi",
  portalApi: "portalApi",
  userProfileApi: "userProfileApi",
} as const

export const apiBaseUrl = `${getApiUrl()}/food/merchant`
export const portalBaseUrl = `${getPortalUrl()}/foodtroy`
export const dpApiMerchantUrl = `${getApiUrl()}/delvplatformapi/merchant`
export const userProfileBaseUrl = `${getTroyUrl()}/user-profile`

export const dpApiEndpoints = {
  getDpApiFeatures: "/v1/features",
  searchOutlets: "/v1/merchant-group/store/search",
  fetchItemsWithoutPhotoCount:
    "/v1/merchant-group/store/merchants-items-without-photo-count",
  fetchMerchantAggregate: "/v1/merchant-group/store/aggregate-by-fields",
  fetchCityList: "/v1/merchant-group/store/citylist",
  fetchOrderExportedFile: "/v1/mexsupplies/invoice/export",
  fetchOrderExportedMpaFile: "/v1/mexsupplies/mpatransaction/export",
}

export const authEndpoints = {
  getUserProfileDetail: "/v2/details",
}

export const endpoints = {
  submitOrderChanges: "/v1/submit-order-changes",
  loadConfig: "/v1/config",
  uploadFile: "/v2/upload-file",
  uploadItemPhoto: "/v2/upload-file",
  uploadMenuGroupFile: "/v1/upload-menu-group-file",
  menuV3EditorStatus: "/v3/menu/editor-status",
  menuV3ModifierGroups: "/v3/menu/modifier-groups",
  menuV3Hierarchies: "/v3/menu/hierarchies",
  menuV3Items: "/v3/menu/items",
  sortDepartmentItems: "/departments/items-sort",
  sortDepartments: "/departments-sort",
  sortDepartmentsMenuGroup: "/v1/menu-groups/departments-sort",
  sortItems: "/items-sort",
  sortItemsMenuGroup: "/v1/menu-groups/items-sort",
  sortCategories: "/categories-sort",
  sortCategoriesMenuGroup: "/v1/menu-groups/categories-sort",
  listSellingTimes: "/v1/menu/selling-times",
  modifierGroupsDetails: "/v1/menu/modifier-groups/details",
  itemDetailsByItemIDs: "/v1/menu/items/details",
  searchItems: "/v1/search-items",
  searchMenusForMex: "/v2/search-menu",
  martItemMenuDisplayV2: "/v2/mart-menu",
  listAttributes: "/v1/attributes",
  listMenuGroupAttributes: "/v2/menu-groups/attributes",
  fetchAttributeClusterOption: "/v1/attributes",
  fetchMenuGroupAttributeClusterOption: "/v2/menu-groups/attributes",
  deleteItem: "/v2/items",
  deleteItemV2: "/v2/items/bulk",
  updateItemsAvailableV1: "/v1/items/available-status",
  updateItemsSellingTimeV2: "/v2/items/sellingTime",
  fetchMenuGroupList: "/v1/menu-groups",
  publishMenuGroup: "/v1/menu-groups",
  upsertItemV2: "/v2/upsert-item",
  upsertItemV2MenuGroup: "/v2/menu-groups/upsert-item",
  upsertItemStock: (itemID: string) =>
    `/v1/items/${encodeURIComponent(itemID)}/upsert-item-stock`,
  getMerchant: "/v2/merchants",
  fetchIncidentList: "/v1/merchant/incidentlist",
  fetchIncidentStat: "/v1/merchant/incidentstats",
  getIncident: "/v1/merchant/incident",
  submitReportReview: "/v1/feedback/report-review",
  postMerchantReviewThrottle: "/v1/feedback/merchant-review-throttles",
  fetchOrderCancelReasons: "/v1/order-unchangeable-reasons",
  fetchOrderSummary: "/v1/merchant-report-summary",
  reviewOrderChanges: "/v1/review-order-changes",
  fetchOrderEta: "/v2/orders-eta",
  cancelOrder: "/v1/cancel-order",
  deleteCategoryV2: "/v2/categories",
  deleteCategoryMenuGroup: "/v1/menu-groups/categories",
  fetchOrderByID: "/v3/orders",
  updateStoreStatus: "/v1/merchant/status",
  fetchStoreOpenStatus: "/v3/open-status",
  markOrder: "/orders/mark",
  deliverByMex: "/v1/deliver-by-mex",
  terminateOrder: "/v3/orders",
  updateExistingTranslation: "/v1/menu-translations",
  updateExistingTranslationMenuGroup: "/v1/menu-groups/menu-translations",
  changeModifierAvailability: "/v2/modifiers/available",
  createMenuGroup: "/v1/menu-groups",
  deleteMenuGroup: (menuGroupID: string) =>
    `/v1/menu-groups/${menuGroupID}/delete`,
  getTranslation: "/v1/menu-translations",
  getTranslationMenuGroup: "/v1/menu-groups/menu-translations",
  linkItemToModifierGroup: "/v2/item-modifier-groups",
  linkItemToModifierGroupMenuGroup: "/v1/menu-groups/item-modifier-groups",
  resetMenuGroup: "/v1/menu-groups",
  editModifierGroup: "/v2/modifier-groups",
  editModifierGroupMenuGroup: "/v1/menu-groups/modifier-groups",
  fetchDepartmentsForGrabCategory: "/v2/item-classes",
  fetchDepartmentsForGrabCategoryMenuGroup: "/v1/menu-groups/item-classes",
  fetchSubdepartmentsForGrabCategory: "/v2/item-classes",
  fetchSubdepartmentsForGrabCategoryMenuGroup: "/v1/menu-groups/item-classes",
  getVerifyTranslation: "/v1/verify-translation",
  getVerifyTranslationMenuGroup: "/v1/menu-groups/verify-translation",
  createModifierGroup: "/v2/modifier-groups",
  createMenuGroupModifierGroup: "/v1/menu-groups/modifier-groups",
  proofModifierGroupMenuGroup: "/v1/menu-groups/modifier-groups",
  proofModifierGroup: "/v2/modifier-groups",
  deleteModifierGroupV2: "/v2/modifier-groups",
  deleteModifierGroupMenuGroup: "/v1/menu-groups/modifier-groups",
  createCategory: "/v2/categories",
  createCategoryMenuGroup: "/v1/menu-groups/categories",
  linkStoreToMenuGroup: "/v1/menu-groups",
  createSection: "/v1/section",
  createSectionMenuGroup: "/v1/menu-groups/section",
  deleteSection: "/v1/section",
  deleteSectionMenuGroup: "/v1/menu-groups/section",
  unlinkStoreFromMenuGroup: "/v1/menu-groups",
  postFeedbackGrabCategory: "/v1/prediction-feedbacks",
  postFeedbackGrabCategoryMenuGroup: "/v1/menu/prediction-feedbacks",
  fetchSupportedAttributeCluster: "/v1/supported-attribute-clusters",
  fetchSupportedAttributeClusterMenuGroup:
    "/v2/menu-groups/supported-attribute-clusters",
  fetchSuggestedDepartments: "/v1/items/department-suggestions",
  fetchSuggestedDepartmentsMenuGroup: "/v1/menu/items/department-suggestions",
  fetchSubDepartmentsV2: "/v2/item-classes",
  fetchSubDepartmentsMenuGroup: "/v1/menu-groups/item-classes",
  getItemPrediction: "/v1/item-prediction",
  getMenuGroupItemPrediction: "/v1/menu-groups/items/item-prediction",
  updateSellingTimeMenuGroup: "/v1/menu-groups/selling-times",
  updateDepartment: "/v1/departments",
  updateDepartmentMenuGroup: "/v1/menu-groups/departments",
  updateMenuGroup: "/v1/menu-groups",
  updateCategoryV3: "/v3/categories",
  updateCategoryV2: "/v2/categories",
  updateCategoryMenuGroupV2: "/v2/menu-groups/categories",
  updateCategoryMenuGroup: "/v1/menu-groups/categories",
  updateSections: "/v1/sections",
  updateSectionsMenuGroup: "/v1/menu-groups/sections",
  createSellingTime: "/v1/menu/selling-times",
  createSellingTimeMenuGroup: "/v1/menu-groups/selling-times",
  deleteSellingTime: "/v1/menu/selling-times",
  enhanceImage: "/v1/enhance-image",
  fetchMenuGroupLinkedStores: (menuGroupID: string) =>
    `/v1/menu-groups/${menuGroupID}/stores`,
  loadMoreDepartmentsForGrabCategory: "/v2/item-classes",
  loadMoreDepartmentsForGrabCategoryMenuGroup: "/v1/menu-groups/item-classes",
  itemValidation: "/v2/item-validation",
  itemValidationMenuGroup: "/v2/menu-groups/item-validation",
  sortMenuEntities: "/v1/menu-entities/sort-order",
  sortMenuEntitiesMenuGroup: "/v1/menu-groups/menu-entities/sort-order",
}

export const foodTroyEndpoints = {
  fetchStoreList: (countryCode: string) =>
    `/v1/${countryCode}/merchant-groups/catalog-stores`,
}

export const bulkUploadEndpoints = {
  getBulkTasks: "/v1/bulktasks",
  createBulkTask: "/v1/bulktasks",
  completeBulkTask: (taskID: string) =>
    `/v1/bulktasks/${taskID}/complete-uploading`,
  fetchInventoryExportedFile: (bulkUploadType: string) =>
    `/v1/${
      bulkUploadType === "inventory" ? "inventory/categories" : "items"
    }/export`,
  fetchInventoryCategory: (bulkUploadType: string) =>
    `/v1/${bulkUploadType === "inventory" ? "inventory" : "menu"}/categories`,
}

// Tag types for RTK Query cache invalidation
export const CACHE_QUERY_TAGS = {
  menuV3Items: "menuV3Items",
  menuV3Hierarchies: "menuV3Hierarchies",
  menuV3ModifierGroups: "menuV3ModifierGroups",
  listSellingTimes: "listSellingTimes",
  searchMenusForMex: "searchMenusForMex",
  martItemMenuDisplayV2: "martItemMenuDisplayV2",
  bulkTasks: "bulkTasks",
} as const

/**
 * RTK Query tags to invalidate when mart partial menu skips a full `fetchCatalogue`
 * (bulk notifications, refresh middleware, etc.).
 */
export const PARTIAL_MENU_REFRESH_TAGS = [
  CACHE_QUERY_TAGS.menuV3Items,
  CACHE_QUERY_TAGS.menuV3Hierarchies,
  CACHE_QUERY_TAGS.menuV3ModifierGroups,
  CACHE_QUERY_TAGS.martItemMenuDisplayV2,
  CACHE_QUERY_TAGS.searchMenusForMex,
]

export const merchantGroupEndpoints = {
  searchStores: dpApiEndpoints.searchOutlets,
} as const

export const MERCHANT_GROUP_CACHE_TAGS = {
  stores: "stores",
} as const

export const uploadFileCategories = {
  reportReviewImg: "report_review_img",
} as const
